import React, { useRef, useState } from "react";
import { parseExcelFile, parseWorkbook } from "./excelParser";
import { fetchRemoteWorkbook } from "./kosApi";

/**
 * Admin modal: upload an .xlsx / .xls or paste a public link, parse it
 * client-side, preview it live on the dashboard, then publish or download.
 */
export default function AdminPanel({
  open,
  onClose,
  onApply, // (dataset) => void   — live-update the dashboard
  onPublish, // (dataset) => Promise — save as shared default
  onDownload, // () => void          — download current dashboard html
}) {
  const [tab, setTab] = useState("upload");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [loaded, setLoaded] = useState(null); // {records, months, periodLabel, sourceLabel}
  const [msg, setMsg] = useState(null); // {type, text}
  const fileRef = useRef(null);

  if (!open) return null;

  const fail = (e) =>
    setMsg({ type: "err", text: e && e.message ? e.message : String(e) });

  async function handleFile(file) {
    if (!file) return;
    if (!/\.(xlsx|xlsm|xls)$/i.test(file.name)) {
      return fail(new Error("Please choose an Excel file (.xlsx, .xlsm or .xls)."));
    }
    setBusy(true);
    setMsg(null);
    try {
      const parsed = await parseExcelFile(file);
      const data = { ...parsed, sourceLabel: file.name };
      setLoaded(data);
      onApply(data);
      setMsg({
        type: "ok",
        text: `Loaded ${parsed.records.length} requests from “${file.name}”. Dashboard updated.`,
      });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function handleLink() {
    const u = link.trim();
    if (!u) return fail(new Error("Paste a direct link to a public .xlsx file."));
    setBusy(true);
    setMsg(null);
    try {
      const buffer = await fetchRemoteWorkbook(u);
      const parsed = parseWorkbook(buffer);
      const name = decodeURIComponent(u.split("?")[0].split("/").pop() || "Live workbook");
      const data = { ...parsed, sourceLabel: name, sourceUrl: u };
      setLoaded(data);
      onApply(data);
      setMsg({
        type: "ok",
        text: `Loaded ${parsed.records.length} requests from the live link. Dashboard updated.`,
      });
    } catch (e) {
      fail(
        new Error(
          (e && e.message ? e.message : "Could not load the link.") +
            " If this is a CORS-restricted host, the backend proxy must be reachable."
        )
      );
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!loaded) return;
    setBusy(true);
    setMsg(null);
    try {
      await onPublish(loaded);
      setMsg({
        type: "ok",
        text: "Published. This dataset is now the shared default for everyone.",
      });
    } catch (e) {
      fail(
        new Error(
          (e && e.message ? e.message : "Publish failed.") +
            " The dashboard still reflects your upload locally."
        )
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kos-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="kos-modal" role="dialog" aria-modal="true">
        <div className="kos-modal-head">
          <span className="kicker">Admin</span>
          <h3>Update dashboard data</h3>
          <button className="x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="kos-modal-body">
          <div className="kos-tabs">
            <button className={tab === "upload" ? "on" : ""} onClick={() => setTab("upload")}>
              Upload file
            </button>
            <button className={tab === "link" ? "on" : ""} onClick={() => setTab("link")}>
              Live link
            </button>
          </div>

          {tab === "upload" && (
            <div
              className={"kos-drop" + (drag ? " drag" : "")}
              onClick={() => fileRef.current && fileRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDrag(false);
                handleFile(e.dataTransfer.files && e.dataTransfer.files[0]);
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xlsm,.xls"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files && e.target.files[0])}
              />
              <b>Click to choose</b> or drag an Excel file here
              <span className="hint">
                Expects the “00 Spend.pro” sheet (Client · Billability · Billed hours · Billed By · Scope flags · Status…)
              </span>
            </div>
          )}

          {tab === "link" && (
            <div className="kos-field">
              <label htmlFor="kosLink">Public Excel link (direct .xlsx URL)</label>
              <input
                id="kosLink"
                className="kos-input"
                type="text"
                placeholder="https://…/KPS Digital Deployment Tracker.xlsx"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLink()}
              />
              <div className="kos-help">
                Must be a directly-downloadable workbook. SharePoint/OneDrive “share”
                links usually need a published/anonymous direct-download URL.
              </div>
              <div className="kos-btn-row">
                <button className="kos-btn primary" onClick={handleLink} disabled={busy}>
                  {busy ? "Loading…" : "Load from link"}
                </button>
              </div>
            </div>
          )}

          {loaded && (
            <div className="kos-summary">
              <b>{loaded.records.length}</b> requests · period{" "}
              <b>{loaded.periodLabel}</b> · source <b>{loaded.sourceLabel}</b>
              <div className="kos-btn-row">
                <button className="kos-btn primary" onClick={handlePublish} disabled={busy}>
                  Publish as shared default
                </button>
                <button className="kos-btn" onClick={onDownload} disabled={busy}>
                  Download HTML
                </button>
              </div>
            </div>
          )}

          {msg && <div className={"kos-msg " + (msg.type === "err" ? "err" : "ok")}>{msg.text}</div>}
        </div>
      </div>
    </div>
  );
}
